import { AlertCircle, AlertTriangle, CheckCircle2, X as CloseIcon, Info } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

export type AlertVariant = "info" | "success" | "warning" | "danger" | "neutral";

export interface AlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "role" | "title"> {
  /** Optional action node, typically a Button. Rendered on the trailing edge. */
  action?: React.ReactNode;
  /** Override the default icon for the variant. Pass `null` to omit the icon. */
  icon?: React.ReactNode | null;
  /** When true, render a close button. Wire `onDismiss` to handle removal. */
  dismissible?: boolean;
  onDismiss?: () => void;
  /**
   * ARIA role override. Defaults to 'status' for info/success/neutral
   * (polite announcement) and 'alert' for warning/danger (assertive).
   */
  role?: "status" | "alert";
  /** Title row. Use a short, action-oriented sentence. */
  title?: React.ReactNode;
  /** Optional second-line description for context or guidance. */
  description?: React.ReactNode;
  variant?: AlertVariant;
}

const ICON_BY_VARIANT: Record<AlertVariant, React.ElementType> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
  neutral: Info,
};

const ASSERTIVE_VARIANTS: AlertVariant[] = ["warning", "danger"];

/**
 * Inline status banner with icon, title, and optional description. Use for
 * persistent contextual messages — never for transient toast notifications.
 * Role is auto-derived from variant: warning/danger announce assertively,
 * info/success/neutral announce politely.
 */
export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  {
    action,
    children,
    className,
    description,
    dismissible,
    icon,
    onDismiss,
    role,
    title,
    variant = "info",
    ...props
  },
  ref,
) {
  const resolvedRole = role ?? (ASSERTIVE_VARIANTS.includes(variant) ? "alert" : "status");
  const IconComponent = ICON_BY_VARIANT[variant];
  const renderedIcon =
    icon === null ? null : icon !== undefined ? (
      icon
    ) : (
      <IconComponent aria-hidden className="cw-v2-alert__icon" />
    );

  return (
    <div
      ref={ref}
      role={resolvedRole}
      aria-live={resolvedRole === "alert" ? "assertive" : "polite"}
      className={cx("cw-v2-alert", `cw-v2-alert--${variant}`, className)}
      {...props}
    >
      {renderedIcon ? (
        <span aria-hidden className="cw-v2-alert__icon-wrap">
          {renderedIcon}
        </span>
      ) : null}
      <div className="cw-v2-alert__body">
        {title ? <div className="cw-v2-alert__title">{title}</div> : null}
        {description ? <div className="cw-v2-alert__description">{description}</div> : null}
        {children ? <div className="cw-v2-alert__content">{children}</div> : null}
      </div>
      {action ? <div className="cw-v2-alert__action">{action}</div> : null}
      {dismissible ? (
        <button
          aria-label="Dismiss"
          className="cw-v2-alert__close"
          onClick={onDismiss}
          type="button"
        >
          <CloseIcon aria-hidden className="cw-v2-alert__close-icon" />
        </button>
      ) : null}
    </div>
  );
});
Alert.displayName = "Alert";

/** Convenience subcomponent for composing custom alert layouts. */
export function AlertTitle({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("cw-v2-alert__title", className)} {...props} />;
}

/** Convenience subcomponent for composing custom alert layouts. */
export function AlertDescription({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("cw-v2-alert__description", className)} {...props} />;
}
